import { formatPriceOrFallback } from "@/lib/hub-course-prices";
import { PATHWAY_PACKAGE_VARIANTS } from "@/lib/pathway-packages";
import {
  formatOnsitePrice,
  isOnsitePriceExVatDisplay,
} from "@/utils/onsite-vat-display";

export type PathwayCostPriceInputs = {
  accessExVatPence: number | null;
  level4ExVatPence: number | null;
  level5ExVatPence: number | null;
  level7DiplomaExVatPence: number | null;
  packageExVatPence: number | null;
  /** Package component ex-VAT pence (BLS, L4, L5, L7 Diploma). */
  packageComponentExVatPence: Array<number | null>;
  /** Full Access-to-Level-7 comparison uses standard prices, including Level 7 at its standard rate. */
  fullPathwayStandardExVatPence?: number | null;
};

function vatWording(): string {
  return isOnsitePriceExVatDisplay()
    ? ", excluding VAT"
    : ", including VAT";
}

/**
 * Visible H2 cost block and FAQ answer text for Pathway.
 * Individual prices come from CRM. Package totals use the agreed entry-level offers.
 */
export function buildPathwayCostAnswerText(
  prices: PathwayCostPriceInputs,
): string {
  const access = formatPriceOrFallback(
    formatOnsitePrice(prices.accessExVatPence),
  );
  const level4 = formatPriceOrFallback(
    formatOnsitePrice(prices.level4ExVatPence),
  );
  const level5 = formatPriceOrFallback(
    formatOnsitePrice(prices.level5ExVatPence),
  );
  const level7 = formatPriceOrFallback(
    formatOnsitePrice(prices.level7DiplomaExVatPence),
  );
  const packages = PATHWAY_PACKAGE_VARIANTS.map(variant => ({
    ...variant,
    label: formatOnsitePrice(Math.round(variant.priceIncVatPence / 1.2)),
  }));
  const parts = [
    `Choose the package for your entry point. ${isOnsitePriceExVatDisplay() ? "Package prices exclude VAT." : "All package prices include VAT."}`,
    ...packages.map(variant => `${variant.title}: ${variant.label}.`),
    `You can also book individual qualifications. Current course prices are Level 3 Access ${access}, Level 4 skin ${level4}, Level 5 skin ${level5} and the next available Level 7 Diploma course ${level7}${vatWording()}.`,
  ];
  if (prices.fullPathwayStandardExVatPence != null) {
    parts.push(`The full Access-to-Level-7 route booked individually at standard course prices totals ${formatOnsitePrice(prices.fullPathwayStandardExVatPence)}${vatWording()}. Package savings are compared with standard individual prices, rather than the next-cohort Level 7 promotion.`);
  }
  return parts.join("\n\n");
}

/** Salary FAQ: no-figures version (no NCS beauty therapist pay figures). */
export const PATHWAY_SALARY_FAQ = {
  question: "How much do aesthetic practitioners earn?",
  answer:
    "Earnings depend on whether you are employed or self-employed, the treatments you offer, your prices and the number of clients you see. A qualification does not guarantee a salary, a client base or a particular level of income.\n\nIf you plan to work for yourself, distinguish treatment revenue from take-home income: products, equipment, premises, insurance and other business costs all affect what you earn. Build your business plan around realistic costs and demand.",
  ncsHref: "https://nationalcareers.service.gov.uk/job-profiles/beauty-therapist",
  salaryGuideHref: "/blog/uk-aesthetics-salary-benchmark-2026/",
} as const;

/**
 * Hub cost FAQ from HubCoursePrice.priceIncVatLabel values.
 * Ends with a pointer to /course-prices/.
 */
export function buildHubCostFaqAnswer(prices: {
  accessLabel: string | null;
  level4Label: string | null;
  level5Label: string | null;
  level7Label: string | null;
}): string {
  const access = formatPriceOrFallback(prices.accessLabel);
  const level4 = formatPriceOrFallback(prices.level4Label);
  const level5 = formatPriceOrFallback(prices.level5Label);
  const level7 = formatPriceOrFallback(prices.level7Label);

  return [
    "Aesthetics training at Cosmetic College is priced per qualification, so you pay for the steps you need.",
    `Level 3 Access to Aesthetics is ${access}, the Level 4 skin certificate is ${level4}, the Level 5 skin certificate is ${level5} and the Level 7 Diploma is ${level7}.`,
    "Snap Finance is available for courses over £500, and in-house 0% payment plans are available on a minimum spend of £3,000.",
    "Every course price, including CPD and online courses, is on our course prices page.",
  ].join(" ");
}
